import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { useProfileStore } from '@/store'
import { Button, ButtonText } from '@/ui/core/button'
import { Header } from '@/ui/core/header'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import * as LocalAuthentication from 'expo-local-authentication'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AppState, type AppStateStatus, Platform, View } from 'react-native'

/** Whether this device can do Face ID (or Touch ID) right now. iOS only; never web or Android. */
export const useBiometrics = () => {
	const [available, setAvailable] = useState(false)
	useEffect(() => {
		if (Platform.OS !== 'ios') return
		Promise.all([
			LocalAuthentication.hasHardwareAsync(),
			LocalAuthentication.isEnrolledAsync(),
		]).then(([hardware, enrolled]) => setAvailable(hardware && enrolled))
	}, [])
	return available
}

/**
 * The lock: with the setting on, every return to the foreground covers the app with the pink
 * ground and asks for Face ID before showing anything. A failed or cancelled prompt leaves the
 * cover up with a button to try again; nothing behind it is reachable.
 */
export const AppLock = ({ children }: { children: React.ReactNode }) => {
	const { faceIdLock } = useProfileStore()
	const [locked, setLocked] = useState(faceIdLock && Platform.OS === 'ios')
	const [prompting, setPrompting] = useState(false)
	const appState = useRef<AppStateStatus>(AppState.currentState)

	const unlock = useCallback(async () => {
		if (prompting) return
		setPrompting(true)
		try {
			const result = await LocalAuthentication.authenticateAsync({
				promptMessage: 'Unlock Wally',
				cancelLabel: 'Not now',
				disableDeviceFallback: false,
			})
			if (result.success) setLocked(false)
		} finally {
			setPrompting(false)
		}
	}, [prompting])

	useEffect(() => {
		if (!faceIdLock || Platform.OS !== 'ios') {
			setLocked(false)
			return
		}
		const sub = AppState.addEventListener('change', (next) => {
			// Lock as soon as the app leaves the foreground, so the switcher snapshot is the cover.
			if (next === 'inactive' || next === 'background') {
				setLocked(true)
			}
			appState.current = next
		})
		return () => sub.remove()
	}, [faceIdLock])

	// The prompt fires once per lock, not again when `unlock` is rebuilt.
	// biome-ignore lint/correctness/useExhaustiveDependencies: fire on lock only
	useEffect(() => {
		if (locked) unlock()
	}, [locked])

	return (
		<View className="flex-1">
			<View
				className="flex-1"
				importantForAccessibility={locked ? 'no-hide-descendants' : 'auto'}
			>
				{children}
			</View>
			{locked && (
				<View
					className="absolute inset-0 items-center justify-center bg-ground px-8"
					accessibilityViewIsModal
				>
					<VStack gap={20} className="items-center">
						<View className="h-[72px] w-[72px] items-center justify-center rounded-full bg-white">
							<Glyph name="sparkle" size={30} color={colors.ink} />
						</View>
						<Header variant="h2" className="text-center">
							{'Locked'}
						</Header>
						<Text size="body1" className="text-center">
							Wally opens with Face ID.
						</Text>
						<Button fullWidth={false} isLoading={prompting} onPress={unlock}>
							<ButtonText>Unlock</ButtonText>
						</Button>
					</VStack>
				</View>
			)}
		</View>
	)
}
